// Copyright (c) Bentley Systems
import { useEffect, useMemo, useState } from "react";
import { Checkbox, DefaultCell, Table } from "@itwin/itwinui-react";
import { Column } from "@itwin/itwinui-react/react-table";
import { EdfsRole } from "./Models/edfs-role";
import { ItwinRole } from "./Models/itwin-role";
import { Assignment } from "./Models/assignments";

type RoleAssignmentTableProps = {
  iTwinRoles: ItwinRole[];
  edfsRoles: EdfsRole[];
  initialAssignments: Assignment[];
  isChanged: (changed: boolean) => void;
  onAssignmentsChange: (newAssignments: Assignment[]) => void;
};

const RoleAssignmentTable: React.FC<RoleAssignmentTableProps> = ({
  iTwinRoles,
  edfsRoles,
  initialAssignments: initialAssignments,
  isChanged,
  onAssignmentsChange,
}) => {
  const [assignments, setAssignments] = useState<{
    [key: string]: { [key: string]: boolean };
  }>({});

  useEffect(() => {
    const initialState = initialAssignments.reduce(
      (acc, assignment) => {
        acc[assignment.iTwinRoleId] = assignment.packageRoles.reduce(
          (roleAcc, role) => {
            roleAcc[role.packageRoleId] = true;
            return roleAcc;
          },
          {} as { [key: string]: boolean },
        );
        return acc;
      },
      {} as { [key: string]: { [key: string]: boolean } },
    );
    setAssignments(initialState);
  }, [initialAssignments]);

  const sortObjectProperties = (obj: {
    [key: string]: any;
  }): { [key: string]: any } => {
    return Object.keys(obj)
      .sort()
      .reduce((acc: { [key: string]: any }, key) => {
        acc[key] = obj[key];
        return acc;
      }, {});
  };

  const sortAssignments = (
    assignmentsPar: Assignment[],
    iTwinRolesPar: ItwinRole[],
    edfsRolesPar: EdfsRole[],
  ) => {
    const sortedITwinRoles = [...iTwinRolesPar].sort((a, b) =>
      a.displayName.localeCompare(b.displayName),
    );
    const sortedEdfsRoles = [...edfsRolesPar].sort((a, b) =>
      a.name.localeCompare(b.name),
    );

    return assignmentsPar.map((assignment) => ({
      ...sortObjectProperties(assignment),
      packageRoles: assignment.packageRoles
        .map((role) => ({
          ...sortObjectProperties(role),
          packageRoleName:
            sortedEdfsRoles.find(
              (edfsRole) => edfsRole.roleId === role.packageRoleId,
            )?.name || role.packageRoleName,
        }))
        .sort((a, b) => a.packageRoleName.localeCompare(b.packageRoleName)),
      iTwinRoleName:
        sortedITwinRoles.find((role) => role.id === assignment.iTwinRoleId)
          ?.displayName || assignment.iTwinRoleName,
    }));
  };

  const handleCheckboxChange = (iTwinRoleId: string, edfsRoleId: string) => {
    setAssignments((prevAssignments) => {
      const newAssignments = {
        ...prevAssignments,
        [iTwinRoleId]: {
          ...prevAssignments[iTwinRoleId],
          [edfsRoleId]: !prevAssignments[iTwinRoleId]?.[edfsRoleId],
        },
      };

      // Create updated assignments with only checked roles
      const updatedAssignments: Assignment[] = iTwinRoles
        .map((role) => ({
          iTwinRoleName: role.displayName,
          iTwinRoleId: role.id,
          packageRoles: edfsRoles
            .filter((edfsRole) => newAssignments[role.id]?.[edfsRole.roleId])
            .map((edfsRole) => ({
              packageRoleId: edfsRole.roleId,
              packageRoleName: edfsRole.name,
            })),
        }))
        .filter((assignment) => assignment.packageRoles.length > 0); // Filter out assignments with no checked roles

      // Compare initialAssignments with updatedAssignments

      const sortedInitialAssignments = sortAssignments(
        initialAssignments,
        iTwinRoles,
        edfsRoles,
      );
      const sortedUpdatedAssignments = sortAssignments(
        updatedAssignments,
        iTwinRoles,
        edfsRoles,
      );

      const changed =
        JSON.stringify(sortedInitialAssignments) !==
        JSON.stringify(sortedUpdatedAssignments);

      isChanged(changed);

      onAssignmentsChange(updatedAssignments); // Call the new callback function with filtered Assignment[]
      return newAssignments;
    });
  };

  // Sort iTwinRoles and edfsRoles alphabetically by displayName and name respectively
  const sortedLocalITwinRoles = useMemo(
    () =>
      [...iTwinRoles].sort((a, b) =>
        a.displayName.localeCompare(b.displayName),
      ),
    [iTwinRoles],
  );

  const sortedLocalEdfsRoles = useMemo(
    () => [...edfsRoles].sort((a, b) => a.name.localeCompare(b.name)),
    [edfsRoles],
  );

  const columns: Column<Record<string, unknown>>[] = useMemo(() => {
    const cols: Column<Record<string, unknown>>[] = [
      {
        Header: "Role",
        accessor: "displayName",
        id: "displayName",
        minWidth: 200,
      },
      ...sortedLocalEdfsRoles.map((edfsRole) => ({
        Header: () => {
          return (
            <div
              style={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
              }}
            >
              <span>{edfsRole.name}</span>
            </div>
          );
        },
        accessor: edfsRole.roleId,
        id: edfsRole.roleId,
        minWidth: 150,
        cellRenderer: (props: any) => (
          <DefaultCell
            {...props}
            style={{
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Checkbox
              checked={
                assignments[props.cellProps.row.original.id]?.[
                  edfsRole.roleId
                ] || false
              }
              onChange={() =>
                handleCheckboxChange(
                  props.cellProps.row.original.id,
                  edfsRole.roleId,
                )
              }
            />
          </DefaultCell>
        ),
      })),
    ];
    return cols;
  }, [sortedLocalEdfsRoles, assignments]);

  const data = useMemo(
    () =>
      sortedLocalITwinRoles.map((role) => ({
        ...role,
        ...sortedLocalEdfsRoles.reduce(
          (acc, edfsRole) => {
            acc[edfsRole.roleId] =
              assignments[role.id]?.[edfsRole.roleId] || false;
            return acc;
          },
          {} as { [key: string]: boolean },
        ),
      })),
    [sortedLocalITwinRoles, sortedLocalEdfsRoles, assignments],
  );

  return (
    <div>
      <Table
        columns={columns}
        data={data}
        emptyTableContent="No roles available."
        density="extra-condensed"
        styleType="zebra-rows"
      />
    </div>
  );
};

export default RoleAssignmentTable;
